/**
 * 预测市场 (provider_code='pm') 专属入场逻辑: launch 拿 launch_url 直接塞 iframe 展示
 *
 * 见 `48-pm-frontend-integration.md`。跟 useGamePlay() (普通厂牌) 的关键区别:
 *
 * 1. 没有 game_code —— 预测市场没有游戏目录, 整个厂牌就是一个入口, launch 时不传。
 * 2. launch_url 一次性、60 秒内有效: 拿到立刻设为 iframe src, 不缓存不预加载。
 * 3. 页面刷新**不能**重新 launch (会重新带入资金), 要用 `/api/v1/game/relaunch`
 *    对同一个 session_id 换一个新地址, 不动资金——这跟视讯"刷新就按退出处理"的
 *    策略正好相反, 别把两套逻辑搞混。为了让刷新后 (页面级别, 不只是组件级别)
 *    还能找回 session_id, 用 sessionStorage 持久化, 不能只放在内存变量里。
 * 4. 嵌入页通过 postMessage 发 ready/height/navigate 三种消息。navigate 的具体
 *    字段后端还没补全 (见文档)。⚠️ 实测踩过坑: 一度按文档建议"分不清就当返回处
 *    理"直接调 exitPm(), 结果嵌入页自己内部路由跳转 (从截图看是个 Next.js app,
 *    从 launch 跳到默认的 sports 页) 也会广播这个消息, 导致玩家一进去就被自动
 *    退出。现在 navigate 只打日志不做动作, 真正的返回/退出靠 GameIframeOverlay
 *    的手动退出按钮兜底, 等后端补全字段、能跟内部路由跳转区分开了再接自动退出。
 *
 * session_id/iframe url 用模块级变量 (不是 per-component 的 ref): 全局退出按钮
 * (GameIframeOverlay 的 @close)、App.vue 的开机恢复逻辑都要跨组件访问同一份状态,
 * 参照 liveLoginBridge.ts/useLiveGamePlay.ts 的 session 共享模式。
 */
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useLobbyStore } from '@/stores/modules/lobby'
import { launchGame, relaunchGame, exitGame, gameHeartbeat } from '@/services/api/game'
import { getAccessToken } from '@/services/request'

export const PM_PROVIDER_CODE = 'pm'

// launch 接口业务失败 (如 200002 已有游戏会话, 200005/200008 厂牌问题等):
// message 直接用后端返回的 msg, 不在前端另建一套码表
export class PmLaunchError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PmLaunchError'
  }
}

const PM_SESSION_STORAGE_KEY = 'pm_session_id'

// 模块级共享状态: iframe url + session id, 跨组件/跨页面只有一份
const pmIframeUrl = ref('')
const pmSessionId = ref('')
const pmEntering = ref(false)

const PM_HEARTBEAT_INTERVAL_MS = 25_000
let pmHeartbeatTimer: ReturnType<typeof setInterval> | null = null

function startPmHeartbeat(sessionId: string): void {
  stopPmHeartbeat()
  pmHeartbeatTimer = setInterval(() => {
    gameHeartbeat(sessionId)
  }, PM_HEARTBEAT_INTERVAL_MS)
}

function stopPmHeartbeat(): void {
  if (pmHeartbeatTimer) {
    clearInterval(pmHeartbeatTimer)
    pmHeartbeatTimer = null
  }
}

function persistPmSession(sessionId: string): void {
  sessionStorage.setItem(PM_SESSION_STORAGE_KEY, sessionId)
}
function clearPersistedPmSession(): void {
  sessionStorage.removeItem(PM_SESSION_STORAGE_KEY)
}
function getPersistedPmSession(): string | null {
  return sessionStorage.getItem(PM_SESSION_STORAGE_KEY)
}

// postMessage 监听: 嵌入页所有消息都带 source: 'pm-merchant-2c'。
// origin 不硬编码具体域名 (测试/生产地址不同), 而是取当前 iframe 实际打开的
// launch_url 的 origin, 自动适配环境。
let pmMessageListenerBound = false

function handlePmMessage(event: MessageEvent): void {
  if (!pmIframeUrl.value) return
  let expectedOrigin: string
  try {
    expectedOrigin = new URL(pmIframeUrl.value).origin
  } catch {
    return
  }
  if (event.origin !== expectedOrigin) return

  const msg = event.data
  if (!msg || msg.source !== 'pm-merchant-2c') return

  switch (msg.type) {
    case 'ready':
      console.log('[预测市场] 嵌入页加载完成')
      break
    case 'height':
      // 当前覆盖层固定全屏 (position:fixed; inset:0), 不是内嵌排版, 暂不需要
      // 跟着这个消息调整高度; 真要做自适应高度时再接
      break
    case 'navigate':
      // ⚠️ 实测踩坑: 曾经按文档建议"分不清就当返回处理", 一收到 navigate 就调
      // exitPm() —— 结果嵌入页自己内部跳转 (比如从 launch 跳到默认的 sports 页,
      // 从截图看是个 Next.js app, 内部路由跳转大概率也会广播这个消息) 也会触发,
      // 导致玩家一进去就被自动退出。具体字段后端还没给全 (见
      // 48-pm-frontend-integration.md 第二节), 在拿到真实字段、能区分"玩家主动
      // 返回"和"嵌入页内部跳转"之前, 这里先只打日志、不做任何动作——宁可漏掉真正
      // 的返回信号 (玩家还有手动退出按钮兜底), 也不能再误杀正常游玩。
      console.log('[预测市场] 收到 navigate 消息 (暂不处理, 等后端补全字段):', msg)
      break
    default:
      break
  }
}

function ensurePmMessageListener(): void {
  if (pmMessageListenerBound) return
  window.addEventListener('message', handlePmMessage)
  pmMessageListenerBound = true
}

export function usePmGamePlay() {
  const lobbyStore = useLobbyStore()
  const { activeCurrency } = storeToRefs(lobbyStore)

  /**
   * 进入预测市场: launch 拿 session_id + launch_url, 立刻塞 iframe。
   */
  async function enterPm(): Promise<void> {
    if (pmEntering.value) return
    pmEntering.value = true
    try {
      const currency = activeCurrency.value || lobbyStore.currencies[0]?.code || 'USDT'
      const res = await launchGame({ provider_code: PM_PROVIDER_CODE, currency, platform: 'mobile' })

      if (!res.success || !res.data || !res.data.launch_url) {
        throw new PmLaunchError(res.error || '进入预测市场失败')
      }

      pmSessionId.value = res.data.session_id
      persistPmSession(res.data.session_id)
      pmIframeUrl.value = res.data.launch_url
      startPmHeartbeat(res.data.session_id)
      ensurePmMessageListener()
    } catch (e) {
      if (e instanceof PmLaunchError) {
        console.error('[预测市场] launch 失败:', e.message)
      } else {
        console.error('[预测市场] 进入失败:', e)
      }
      throw e
    } finally {
      pmEntering.value = false
    }
  }

  return { pmIframeUrl, pmEntering, enterPm, exitPm }
}

/**
 * 退出预测市场: 关闭 iframe + 调退出接口回收资金。
 * 挂在 GameIframeOverlay 的 @close 上, 也是 navigate 消息兜底处理的落点。
 */
export async function exitPm(): Promise<void> {
  stopPmHeartbeat()
  const sid = pmSessionId.value || getPersistedPmSession()
  pmIframeUrl.value = ''
  pmSessionId.value = ''
  clearPersistedPmSession()
  if (!sid) return
  try {
    await exitGame(sid)
  } catch (e) {
    console.warn('[预测市场] 退出接口调用失败:', e)
  }
}

/**
 * 应用启动时调用一次: 发现本地残留的 session_id (说明是刷新页面/重新打开标签页)
 * 就用 relaunch 换一个新地址续上, 不重新带入资金; 会话已经结束 (200006 等)
 * 就清掉本地残留, 当作正常没有预测市场会话处理。
 */
export async function tryRecoverPmSession(): Promise<void> {
  const sid = getPersistedPmSession()
  if (!sid) return

  // 没有有效登录态就别调了 (relaunch 需要 JWT), 残留的 session_id 留着等真的登录后
  // 再处理即可——这里只是避免每次刷新没登录都打一发注定 1001 的请求
  if (!getAccessToken()) return

  pmSessionId.value = sid
  try {
    const res = await relaunchGame({ session_id: sid })
    if (res.success && res.data?.launch_url) {
      pmIframeUrl.value = res.data.launch_url
      startPmHeartbeat(sid)
      ensurePmMessageListener()
    } else {
      console.warn('[预测市场] 恢复会话失败, 按已结束处理:', res.error)
      pmSessionId.value = ''
      clearPersistedPmSession()
    }
  } catch (e) {
    console.error('[预测市场] 恢复会话异常:', e)
    pmSessionId.value = ''
    clearPersistedPmSession()
  }
}
